<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import CatalogItemForm from './CatalogItemForm.vue';

const props = defineProps({
  selectedItem: {
    type: Object,
    default: () => ({}),
  },
  type: {
    type: String,
    default: 'create',
    validator: value => ['create', 'edit'].includes(value),
  },
  itemType: {
    type: String,
    default: 'product',
    validator: value => ['product', 'service'].includes(value),
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const isSyncingMedia = ref(false);

const i18nKey = computed(
  () => `CAPTAIN.CATALOG_ITEMS.${props.type.toUpperCase()}`
);

const typeKey = computed(() =>
  props.itemType === 'service' ? 'SERVICES' : 'PRODUCTS'
);

const dialogTitle = computed(() =>
  props.type === 'edit'
    ? t(`CAPTAIN.CATALOG_ITEMS.${typeKey.value}.EDIT_TITLE`)
    : t(`CAPTAIN.CATALOG_ITEMS.${typeKey.value}.CREATE_TITLE`)
);

const saveItem = item =>
  props.type === 'edit'
    ? store.dispatch('captainCatalogItems/update', {
        id: props.selectedItem.id,
        ...item,
      })
    : store.dispatch('captainCatalogItems/create', item);

// One at a time and on purpose: files go to a single item, and a video that fails must
// fail on its own rather than race the photos next to it.
const runInSequence = (entries, task) =>
  entries.reduce(
    (chain, entry) =>
      chain.then(async failures => {
        try {
          await task(entry);
        } catch (error) {
          failures.push(error);
        }
        return failures;
      }),
    Promise.resolve([])
  );

const syncMedia = async (itemId, pendingFiles, removedMediaIds) => {
  const deleteFailures = await runInSequence(removedMediaIds, attachmentId =>
    store.dispatch('captainCatalogItems/deleteMedia', {
      id: itemId,
      attachmentId,
    })
  );
  const uploadFailures = await runInSequence(pendingFiles, file =>
    store.dispatch('captainCatalogItems/uploadMedia', { id: itemId, file })
  );

  return [...deleteFailures, ...uploadFailures];
};

const handleSubmit = async ({ item, pendingFiles, removedMediaIds }) => {
  let savedItem;
  try {
    savedItem = await saveItem(item);
  } catch (error) {
    useAlert(
      parseAPIErrorResponse(error) || t(`${i18nKey.value}.ERROR_MESSAGE`)
    );
    return;
  }

  isSyncingMedia.value = true;
  // The item is saved by now. A file that fails is reported on its own instead of undoing
  // the item, which the admin would otherwise have to type in all over again.
  const failures = await syncMedia(savedItem.id, pendingFiles, removedMediaIds);
  isSyncingMedia.value = false;

  if (failures.length) {
    useAlert(
      parseAPIErrorResponse(failures[0]) ||
        t('CAPTAIN.CATALOG_ITEMS.FORM.MEDIA.UPLOAD_ERROR')
    );
  } else {
    useAlert(t(`${i18nKey.value}.SUCCESS_MESSAGE`));
  }

  dialogRef.value.close();
};

const handleClose = () => emit('close');

const handleCancel = () => {
  dialogRef.value.close();
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    :title="dialogTitle"
    :description="t('CAPTAIN.CATALOG_ITEMS.FORM_DESCRIPTION')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="handleClose"
  >
    <CatalogItemForm
      :mode="type"
      :item-type="itemType"
      :item="selectedItem"
      @submit="handleSubmit"
      @cancel="handleCancel"
    />
    <p
      v-if="isSyncingMedia"
      class="flex items-center gap-2 mt-3 text-sm text-n-slate-11"
    >
      <span class="i-lucide-loader-2 animate-spin" />
      {{ t('CAPTAIN.CATALOG_ITEMS.FORM.MEDIA.UPLOADING') }}
    </p>

    <template #footer />
  </Dialog>
</template>
